import { TransactionReceipt, TransactionRequest, JsonRpcSigner, verifyMessage, toBeArray } from "ethers";
import { EVMChainConfig } from "@shared/types";

// https://eips.ethereum.org/EIPS/eip-3085
type AddEthereumChainParameter = {
    chainId: string;
    blockExplorerUrls?: string[];
    chainName?: string;
    iconUrls?: string[];
    nativeCurrency?: {
        name: string;
        symbol: string;
        decimals: number;
    };
    rpcUrls?: string[];
};

export const _getEVMChainParamFromChainConfig = (chainConfig: EVMChainConfig): AddEthereumChainParameter => ({
    chainId: `0x${chainConfig.chain_id.toString(16)}`,
    blockExplorerUrls: [chainConfig.explorer ?? ""],
    chainName: chainConfig.network,
    iconUrls: [chainConfig.image ?? ""],
    nativeCurrency: chainConfig.native_currency,
    rpcUrls: [chainConfig.rpc ?? ""],
});

export const getChainIdFromSigner = async (signer: JsonRpcSigner): Promise<number> => {
    if (!signer.provider) {
        throw new Error("Signer does not have a provider");
    }

    const network = await signer.provider.getNetwork();
    return Number(network.chainId.toString());
};

export const addChain = async (signer: JsonRpcSigner, chainConfig: AddEthereumChainParameter) => {
    if (!signer.provider) {
        throw new Error("Signer does not have a provider");
    }

    await signer.provider.send("wallet_addEthereumChain", [chainConfig]);
};

export const switchChain = async (chainId: number, signer: JsonRpcSigner, chainConfig?: EVMChainConfig) => {
    if (!signer.provider) {
        throw new Error("Signer does not have a provider");
    }

    try {
        await signer.provider.send("wallet_switchEthereumChain", [{ chainId: `0x${chainId.toString(16)}` }]);
    } catch (error) {
        console.error("Error switching chain", error);
        if (chainConfig) {
            try {
                await addChain(signer, _getEVMChainParamFromChainConfig(chainConfig));
                await signer.provider.send("wallet_switchEthereumChain", [{ chainId: `0x${chainId.toString(16)}` }]);
            } catch (error) {
                console.error("Error adding chain", error);
                throw error;
            }
        } else {
            throw error;
        }
    }
};

/**
 * Send a transaction using the signer.
 *
 * @param {JsonRpcSigner} signer {{@link JsonRpcSigner}} The signer to send the transaction.
 * @param {TransactionRequest} transaction {{@link TransactionRequest}} The transaction to send.
 * @param {number} confirmations {{@link number}} (optional) The number of confirmations to wait for default to 1.
 * @param {number} timeout {{@link number}} (optional) The timeout to wait for.
 * @returns {Promise<TransactionReceipt | null>} ``receipt`` {{@link TransactionReceipt}} The transaction receipt.
 *
 * If confirmations is 0, the promise will resolve to ``null``.
 * Check the {@link https://docs.ethers.org/v6/api/providers/#TransactionResponse-wait ethers v6 documentation} for more information.
 */
export const sendTransaction = async <C extends number = 1>({
    signer,
    transaction,
    confirmations,
    timeout,
}: {
    signer: JsonRpcSigner;
    transaction: TransactionRequest;
    confirmations?: C;
    timeout?: number;
}): Promise<C extends 0 ? null : TransactionReceipt> => {
    const txResponse = await signer.sendTransaction(transaction);

    return (await txResponse.wait(confirmations, timeout)) as C extends 0 ? null : TransactionReceipt;
};

export const getSignerAddressFromMessage = (message: string, signedMessage: string) =>
    verifyMessage(toBeArray(message), signedMessage);
